import { ContactHistoDialogResolver } from '@agent/agent-interface/contact-view/contact-view-histo-popin.component.ajs';
import { AgentStatusDeprecatedService } from '@agent/core/agent/agent-status-deprecated.service';
import { IOutgoingMailDeprecatedFacade } from '@agent/core/channel-digit/mail/outgoing-mail-deprecated.service';
import { AppointmentDeprecatedService } from '@agent/core/contact/appointment-deprecated.service';
import { ContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ContactHistoryService } from '@agent/core/contact/history/contact-history.service';
import { UnitOutgoingCallDeprecatedService } from '@agent/core/phone/outcall/unit-outgoing-call-deprecated.service';
import { ModalService } from '@commons/core/ui/modal/modal.service';
import { CustomerCardsDeprecatedService } from '@commons/customer/customer-cards-deprecated.service';
import { TimelineEntry } from '@commons/history/timeline/models/timeline-entry';
import { isObject, isString } from 'angular';
import { i18n } from 'i18next';
import { filter, Subscription, take, timeout } from 'rxjs';

import {
  AgentDesktopActions,
  AgentStatus,
  ChannelUtil,
  coerceCustomerId,
  coerceExchangeId,
  ContactDoc,
  ContactEntryDoc,
  Customer,
  CustomerFacade,
  CustomerInput,
  ExchangeInput,
  HistoryDialogUtil,
  isNotNullOrUndefined,
  joiner,
  MediaType,
  None,
  Option,
  SmsFacade,
  ToasterFacade,
  UserUtil,
  ElasticSearchService,
} from '@contact/upgrade';

const MODULE_NAME = 'helpzoneSearchService';

export class HelpzoneSearchDeprecatedService implements AgentDesktopActions {
  static $inject: Array<string> = [
    '$i18next',
    'toasterFacade',
    'contactDeprecatedService',
    'agentStatusDeprecatedService',
    'outgoingMailDeprecatedService',
    'unitOutgoingCallDeprecatedService',
    'appointmentDeprecatedService',
    'customerFacade',
    'modalService',
    'agentHistory',
    'customerCardsDeprecatedService',
    'smsFacade',
    'elasticSearchService',
  ];

  public name = MODULE_NAME;

  private _openHistoSub = Subscription.EMPTY;

  constructor(
    private readonly $i18next: i18n,
    private readonly toasterFacade: ToasterFacade,
    private readonly contactDeprecatedService: ContactDeprecatedService,
    private readonly agentStatusDeprecatedService: AgentStatusDeprecatedService,
    private readonly outgoingMailDeprecatedFacade: IOutgoingMailDeprecatedFacade,
    private readonly unitOutgoingCallDeprecatedService: UnitOutgoingCallDeprecatedService,
    private readonly appointmentDeprecatedService: AppointmentDeprecatedService,
    private readonly customerFacade: CustomerFacade,
    private readonly modalService: ModalService,
    private readonly contactHistoryService: ContactHistoryService,
    private readonly customerCardsDeprecatedService: CustomerCardsDeprecatedService,
    private readonly smsFacade: SmsFacade,
    private readonly elasticSearchService: ElasticSearchService
  ) {}

  openCustomerHistory(customerInput: CustomerInput, exchangeInput?: ExchangeInput) {
    if (!customerInput) {
      return;
    }
    const customerId = coerceCustomerId(customerInput);
    if (exchangeInput) {
      if (!this.isContactEntryDoc(exchangeInput)) {
        const exchangeId = coerceExchangeId(exchangeInput);
        this.elasticSearchService
          .searchOneContactEntry({ id: exchangeId })
          .pipe(isNotNullOrUndefined())
          .subscribe(entity =>
            this.openHistoForCustomer(customerId, customer => ({
              selectContactEntry: () => TimelineEntry.fromContactEntry(ContactHistoryService.buildEntry(entity)),
              contactCalling: () =>
                Option.of({
                  ...this.contactDeprecatedService.buildFakeContact(customerId),
                  customerName: joiner([UserUtil.fullname(customer), customer.companyname], ' ') ?? '',
                }),
            }))
          );
        return;
      }
      this.openHistoForCustomer(customerId, customer => ({
        selectContactEntry: () => TimelineEntry.fromContactEntry(ContactHistoryService.buildEntry(exchangeInput)),
        contactCalling: () =>
          Option.of({
            ...this.contactDeprecatedService.buildFakeContact(customerId),
            customerName: joiner([UserUtil.fullname(customer), customer.companyname], ' ') ?? '',
          }),
      }));
      return;
    }
    this.openHistoForCustomer(customerId, customer => ({
      contactCalling: () =>
        Option.of({
          ...this.contactDeprecatedService.buildFakeContact(customerId),
          customerName: joiner([UserUtil.fullname(customer), customer.companyname], ' ') ?? '',
        }),
    }));
  }

  openExchangeHistory(input: ExchangeInput): void {
    if (!input) {
      return;
    }
    const exchangeId = coerceExchangeId(input);
    const resolver: ContactHistoDialogResolver = {
      selectContactEntry: () => TimelineEntry.fromContactEntry(ContactHistoryService.buildEntry(input)),
      checkVisibilityMail: () => Option.of(false),
      inProcessActionsAllowed: () => Option.of(true),
    };
    if (isString(input)) {
      this.openHisto(exchangeId, {
        ...resolver,
        contactCalling: () => None,
        simplePanelModeApplied: () => Option.of(true),
      });
      return;
    }
    if (HistoryDialogUtil.isEntryDoc(input)) {
      const customerId = input.contactUuid;
      const customerName =
        UserUtil.fullname({
          firstname: input.contactFirstName,
          lastname: input.contactLastName,
        }) ?? '';
      if (!customerId) {
        this.openHisto(exchangeId, {
          ...resolver,
          contactCalling: () => None,
          simplePanelModeApplied: () => Option.of(true),
        });
        return;
      }
      this.openHisto(exchangeId, {
        ...resolver,
        contactCalling: () =>
          Option.of({
            ...this.contactDeprecatedService.buildFakeContact(customerId),
            customerName,
          }),
      });
      return;
    }
    if (HistoryDialogUtil.isExchange(input)) {
      const customer = input.customer;
      const customerName = UserUtil.fullname(customer) ?? '';
      if (!customer.id) {
        this.openHisto(exchangeId, {
          ...resolver,
          contactCalling: () => None,
          simplePanelModeApplied: () => Option.of(true),
        });
        return;
      }
      this.openHisto(exchangeId, {
        ...resolver,
        contactCalling: () =>
          Option.of({
            ...this.contactDeprecatedService.buildFakeContact(customer.id),
            customerName,
          }),
      });
      return;
    }
  }

  private openHistoForCustomer(customerId: string, resolve: (customer: Customer) => ContactHistoDialogResolver): void {
    const customer$ = this.customerFacade.getById(customerId);
    customer$
      .pipe(
        take(1),
        filter(customer => !customer)
      )
      .subscribe(() => this.customerFacade.load(customerId));
    this._openHistoSub.unsubscribe();
    this._openHistoSub = customer$
      .pipe(isNotNullOrUndefined(), take(1), timeout(5000))
      .subscribe(customer => this.openHisto(customerId, resolve(customer), customer));
  }

  private openHisto(id: string, resolve: ContactHistoDialogResolver, customer?: Customer): void {
    let customerCompanyName = '';
    if (!!customer && customer.companyname) {
      customerCompanyName = `- ${customer.companyname}`;
    }
    let title = '';
    if (!!customer && (customer.lastname || customer.firstname)) {
      title = `${customer.lastname} ${customer.firstname} ${customerCompanyName}`;
    } else {
      title = `${this.$i18next.t('agent:contact.info.unknown_contact')} `;
    }
    this.modalService.open({
      keyboard: false,
      backdrop: false,
      fullscreen: true,
      ctrlKeyboard: true,
      component: 'contactContactViewHistoPopin',
      title: `${this.$i18next.t('agent:contact.info.popin.title')} ${title}`,
      modalId: `${this.$i18next.t('agent:contact.info.popin.title')}_${id}`,
      resolve,
    });
  }

  askPhoneUnit(customerInput: CustomerInput): void {
    if (!customerInput) {
      return;
    }
    const customerId = coerceCustomerId(customerInput);
    this.unitOutgoingCallDeprecatedService
      .openUnitOutgoingCallFormForContact(customerId)
      .then(() => this.unitOutgoingCallDeprecatedService.finishAndClosePauseAndTask());
  }

  askMailUnit(customerInput: CustomerInput): void {
    if (!customerInput) {
      return;
    }
    if (!this.isCsrAvailableOrWorking()) {
      this.toasterFacade.notification({
        message: 'agent:toast.become_available',
        duration: 5000,
      });
    } else {
      const customerId = coerceCustomerId(customerInput);
      this.outgoingMailDeprecatedFacade.openOutgoingMailForContactUuidAndIasId(customerId, null, null);
    }
  }

  createAppointment(customerInput: CustomerInput): void {
    if (!customerInput) {
      return;
    }
    if (!this.isContactDoc(customerInput)) {
      const customerId = coerceCustomerId(customerInput);
      this.appointmentDeprecatedService.openAppointmentFormForContact(customerId);
      return;
    }
    const [phone] = customerInput.addresses.filter(({ channel }) => ChannelUtil.from(channel)?.type === MediaType.PHONE);
    if (!phone) {
      return;
    }
    this.appointmentDeprecatedService.openAppointmentFormForContact(
      customerInput.uuid,
      phone.uri,
      customerInput.contactFirstName,
      customerInput.contactLastName
    );
  }

  openExchangeInNewWindow(input: ExchangeInput): void {
    if (!this.isContactEntryDoc(input)) {
      return;
    }
    this.contactHistoryService.openExchangeInNewWindow(ContactHistoryService.buildEntry(input));
  }

  linkExchangeToCurrentBooking(exchangeInput: ExchangeInput, customerInput: CustomerInput) {
    if (!this.isContactEntryDoc(exchangeInput)) {
      return;
    }
    if (!this.isContactDoc(customerInput)) {
      return;
    }
    this.customerCardsDeprecatedService.linkContactEntryToContact(exchangeInput.uuid, customerInput.uuid).then(
      response => {
        if (response.data === 'true') {
          this.toasterFacade.notification({
            message: 'agent:toast.interaction_link_success',
            duration: 5000,
          });
        } else {
          this.toasterFacade.alert({ message: 'agent:toast.interaction_link_error' });
        }
      },
      () => this.toasterFacade.alert({ message: 'agent:toast.interaction_link_server_not_reachable' })
    );
  }

  private isCsrAvailableOrWorking(): boolean {
    return (
      this.agentStatusDeprecatedService.getCurrentAgentState() === AgentStatus.AVAILABLE ||
      this.agentStatusDeprecatedService.getCurrentAgentState() === AgentStatus.WORKING
    );
  }

  askSms(customerInput: CustomerInput): void {
    const customerId = coerceCustomerId(customerInput);
    this.smsFacade.openDialog({
      customerId,
    });
  }

  openCustomerInfo(customerInput: CustomerInput): void {
    const customerId = coerceCustomerId(customerInput);
    this.customerFacade.updateDialog(customerId);
  }

  private isContactDoc(obj: any): obj is ContactDoc {
    return isObject(obj) && 'uuid' in obj;
  }

  private isContactEntryDoc(obj: any): obj is ContactEntryDoc {
    return isObject(obj) && 'uuid' in obj;
  }
}

export default MODULE_NAME;
